import { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import { loadConfig, visible, type ActionSpec } from '@duty/shared';
import { InsightCard } from './InsightCard';
import { useIncidentStore } from '@/incident/store';
import { useRole } from '@/auth/role';
import { useMotionReduced } from '@/hooks/useMotionReduced';

const config = loadConfig();

const LIFE_STATUS_LABELS: Record<string, string> = {
  'accounted-for': 'All persons accounted for',
  'building-clear': 'Building confirmed clear',
  'unknown': 'Occupancy unknown',
};

/** The live, ranked, capped insight stream driven by the rule engine. */
export function InsightStream() {
  const { insights, acknowledgeInsight, dismissInsight, dispatch } = useIncidentStore();
  const { can } = useRole();
  const canRespond = can('insight.respond');
  const reduced = useMotionReduced();
  const [expanded, setExpanded] = useState(false);

  const handleAction = useCallback((spec: ActionSpec) => {
    switch (spec.type) {
      case 'set-life-status':
        dispatch({ type: 'update-itasc', patch: { threats: { life: LIFE_STATUS_LABELS[spec.value] ?? spec.value } } });
        break;
      case 'set-strategy':
        dispatch({ type: 'update-itasc', patch: { actions: { strategy: spec.value as never } } });
        break;
      case 'set-stage':
        dispatch({ type: 'set-stage', stage: spec.stage });
        break;
      case 'open-panel': {
        const labelMap: Record<string, string> = {
          water: 'Water plan',
          resources: 'Responding resources',
          itasc: 'Capture ITASC',
        };
        const label = labelMap[spec.panel];
        if (label) {
          document.querySelector(`[aria-label="${label}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      }
    }
  }, [dispatch]);

  const result = visible(insights, config.insights.maxVisibleCards);
  const { shown, suppressed, suppressedByPriority } = result;
  const dismissed = insights.filter((i) => i.status === 'dismissed').length;

  // In expanded mode, also show the suppressed cards after the pinned ones.
  const displayCards = expanded ? [...shown, ...suppressed] : shown.slice(0, config.insights.maxPinnedCards);
  const overflowCount = shown.length - config.insights.maxPinnedCards;
  const hasOverflow = !expanded && overflowCount > 0;

  if (shown.length === 0 && suppressed.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border p-3 text-xs text-muted">
        No active insights for the current context. Cards appear as the incident and ITASC evolve.
      </p>
    );
  }

  return (
    <div className="space-y-2.5" aria-live="polite" aria-label="Active insights">
      <AnimatePresence initial={false}>
        {displayCards.map((c, i) => (
          <InsightCard
            key={c.id}
            insight={c}
            index={i}
            canRespond={canRespond}
            onAcknowledge={acknowledgeInsight}
            onDismiss={dismissInsight}
            onAction={handleAction}
          />
        ))}
      </AnimatePresence>

      {/* Priority suppression fold */}
      {suppressedByPriority && !expanded && (
        <motion.div
          initial={reduced ? undefined : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-2 text-[11px] text-amber-600 dark:text-amber-400"
        >
          <ShieldAlert size={12} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            <strong>{suppressed.length} lower-priority item{suppressed.length !== 1 ? 's' : ''} suppressed</strong>
            {' — '}resolve life safety and firefighter safety concerns first.{' '}
            <button
              onClick={() => setExpanded(true)}
              className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/60 rounded"
            >
              Show anyway
            </button>
          </span>
        </motion.div>
      )}

      {/* Expand / collapse overflow within the shown set */}
      {(hasOverflow || (expanded && (overflowCount > 0 || suppressed.length > 0))) && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-[11px] text-muted hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          aria-expanded={expanded}
        >
          {expanded ? (
            <><ChevronUp size={12} aria-hidden /> Show fewer</>
          ) : (
            <><ChevronDown size={12} aria-hidden /> Show {overflowCount} more</>
          )}
        </button>
      )}

      {/* Footer counts */}
      {dismissed > 0 && (
        <p className="text-[11px] text-muted">{dismissed} dismissed</p>
      )}
    </div>
  );
}
